import { useReducer, useState, useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'
import { isMapOrNone } from '@aytm/app/stats/page/components/lib/utils'

import { CHART_HEIGHT } from '@aytm/app/stats/page/question-views/emd/lib/constants'
import { initState, reducer } from '@aytm/app/stats/page/question-views/emd/lib/state'
import useEmotionalDialState from '@aytm/app/stats/page/question-views/emd/states/useEmotionalDialState'

import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'

import Video from '@aytm/app/stats/page/question-views/emd/components/video/Video'
import Controls from '@aytm/app/stats/page/question-views/emd/components/Controls'
import Chart from '@aytm/app/stats/page/question-views/emd/components/chart/Chart'
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'

import { useComponentWidth } from '@aytm/app/stats/page/question-views/components/charts/lib/hooks'

// First paint, before the report column is read.
const DEFAULT_WIDTH = 814

const EmotionalDialBlock = ({
  questionId,
  questionKey,
  qType,
  params,
  answer,
  batches,
  video,
  calcBadData,
  videoDuration,
  chartMode,
  pageChartMode,
  components,
  colorTheme,
  features,
  disabled,
  onReady,
  onStateChange,
  onChartReady
}) => {
  // the layers are drawn to the report column, which the hook reads for itself
  const { width } = useComponentWidth({ defaultWidth: DEFAULT_WIDTH })

  // the question's own rule, applied to the payload on screen -- read here rather than taken as a
  // flag, so what the block draws can never disagree with what it was handed
  const badData = Boolean(calcBadData?.(answer))

  const { viewMode, lineMode, videoTime, onModelChange } = useEmotionalDialState({
    params,
    onReady,
    onStateChange
  })

  // the persisted position seeds the reducer once; `state.videoTime` is the live value
  // thereafter — the block owns it, so later changes don't re-seed.
  const [state, dispatch] = useReducer(reducer, { videoTime }, initState)
  const [videoEl, setVideoEl] = useState(null)

  // playing mirrored to a ref so the persist decision in handleTimeChange (a stable callback)
  // sees the live value: transient during playback (chart-driven) vs a real seek when paused.
  const playingRef = useRef(state.playing)
  useEffect(() => {
    playingRef.current = state.playing
  }, [state.playing])

  const handleTimeChange = useCallback(
    time => {
      dispatch({ type: 'SET_VIDEO_TIME', value: time })
      if (!playingRef.current) onModelChange?.({ videoTime: time })
    },
    [onModelChange]
  )

  const handlePlayingChange = useCallback(value => dispatch({ type: 'SET_PLAYING', value }), [])
  const handlePlayingEnd = useCallback(() => dispatch({ type: 'SET_PLAYING', value: false }), [])
  const handleTimeVals = useCallback(value => dispatch({ type: 'SET_TIME_VALS', value }), [])

  // Switching view/line mode pauses playback and persists via the coffee shim, which re-renders
  // with the new (controlled) viewMode/lineMode prop.
  const handleControlsChange = useCallback(
    next => {
      dispatch({ type: 'SET_PLAYING', value: false })
      onModelChange?.(next)
    },
    [onModelChange]
  )

  return (
    <BlockWrapper answer={answer}>
      {isComponentOn(components, 'video') && (
        <Video
          video={video}
          width={width}
          videoTime={state.videoTime}
          timeVals={state.timeVals}
          onTimeChange={handleTimeChange}
          onPlayingChange={handlePlayingChange}
          viewMode={viewMode}
          lineMode={lineMode}
          containerRef={setVideoEl}
        />
      )}
      {!badData && !isMapOrNone(chartMode, pageChartMode) && (
        <>
          {isComponentOn(components, 'controls') && (
            <Controls
              model={{ batches, viewMode, lineMode }}
              disabled={disabled}
              onModelChange={handleControlsChange}
            />
          )}
          {isComponentOn(components, 'chart') && (
            <ChartPlaceholder height={CHART_HEIGHT} required isLoading={'loading' === answer}>
              <Chart
                questionKey={questionKey}
                qType={qType}
                questionId={questionId}
                viewMode={viewMode}
                lineMode={lineMode}
                answer={answer}
                batches={batches}
                width={width}
                height={CHART_HEIGHT}
                videoDuration={videoDuration}
                videoEl={videoEl}
                videoTime={state.videoTime}
                playing={state.playing}
                onTimeUpdate={handleTimeChange}
                onPlayingEnd={handlePlayingEnd}
                onTimeValsChange={handleTimeVals}
                onChartReady={onChartReady}
                colorTheme={colorTheme}
                features={features}
              />
            </ChartPlaceholder>
          )}
        </>
      )}
    </BlockWrapper>
  )
}

EmotionalDialBlock.propTypes = {
  questionId: PropTypes.string,
  questionKey: PropTypes.string,
  qType: PropTypes.string,
  params: PropTypes.shape({ emd: PropTypes.object }),
  answer: answerPropType,
  batches: PropTypes.array,
  video: PropTypes.object,
  calcBadData: PropTypes.func,
  videoDuration: PropTypes.number,
  chartMode: PropTypes.string,
  pageChartMode: PropTypes.string,
  components: PropTypes.object,
  colorTheme: PropTypes.array,
  features: PropTypes.object,
  disabled: PropTypes.bool,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func,
  onChartReady: PropTypes.func
}

EmotionalDialBlock.displayName = 'EmotionalDialBlock'

export default EmotionalDialBlock
